import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { FormField } from '@/components/forms/FormField';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { InvoiceTemplatePreview } from '@/components/invoice/InvoiceTemplatePreview';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { useSignatures } from '@/composables/shared/useSignatures';
import { organizationSettingsService } from '@/services/organizationSettings.service';
import { signatureService } from '@/services/signature.service';
import { invoiceSettingsSchema, type InvoiceSettingsFormData } from '@/lib/validators';
import type { OrganizationSettings } from '@/types/table';
import { NumberPreview } from './_shared';

/** Invoice tab — invoice numbering marker, footer, payment defaults, live preview. */
export function InvoiceTab() {
  const { settings, update } = useOrganizationSettings();
  const { defaultSignature } = useSignatures();

  const form = useForm<InvoiceSettingsFormData>({
    resolver: zodResolver(invoiceSettingsSchema),
    defaultValues: {
      invoicePrefix: 'INV',
      nextInvoiceSequence: 1,
      invoiceFooterText: 'Thank you for your business!',
    },
  });

  useEffect(() => {
    if (!settings) return;
    form.reset({
      invoicePrefix: settings.invoicePrefix ?? 'INV',
      nextInvoiceSequence: settings.nextInvoiceSequence ?? 1,
      invoiceFooterText: settings.invoiceFooterText ?? '',
      bankName: settings.bankName ?? '',
      bankAccountName: settings.bankAccountName ?? '',
      bankAccountNumber: settings.bankAccountNumber ?? '',
      bankIfsc: settings.bankIfsc ?? '',
      bankSwift: settings.bankSwift ?? '',
      upiId: settings.upiId ?? '',
      paypalEmail: settings.paypalEmail ?? '',
      paymentInstructions: settings.paymentInstructions ?? '',
    });
  }, [settings, form]);

  const values = form.watch();
  const cacheBuster = settings ? new Date(settings.updatedAt).getTime() : 0;
  const logoUrl = settings?.logoFileKey ? organizationSettingsService.assetUrl('logo', cacheBuster) : null;
  const signatureUrl = defaultSignature ? signatureService.assetUrl(defaultSignature.id, new Date(defaultSignature.updatedAt).getTime()) : null;

  const onSubmit = (data: InvoiceSettingsFormData) => update.mutate(data as Partial<OrganizationSettings>);

  const previewProps = useMemo(() => ({
    kind: 'invoice' as const,
    primaryColor: settings?.primaryColor || '#010b17',
    accentColor: settings?.accentColor || '#a855f7',
    highlightColor: settings?.highlightColor || '#22d3ee',
    companyName: settings?.companyName ?? '',
    companyEmail: settings?.companyEmail,
    companyPhone: settings?.companyPhone,
    companyWebsite: settings?.companyWebsite,
    taxId: settings?.taxId,
    addressLine1: settings?.addressLine1,
    addressLine2: settings?.addressLine2,
    city: settings?.city,
    state: settings?.state,
    country: settings?.country,
    postalCode: settings?.postalCode,
    signatoryName: defaultSignature?.signatoryName,
    signatoryTitle: defaultSignature?.signatoryTitle ?? undefined,
    invoiceFooterText: values.invoiceFooterText,
    bankName: values.bankName,
    bankAccountName: values.bankAccountName,
    bankAccountNumber: values.bankAccountNumber,
    bankIfsc: values.bankIfsc,
    upiId: values.upiId,
    paypalEmail: values.paypalEmail,
    paymentInstructions: values.paymentInstructions,
    logoUrl,
    signatureUrl,
  }), [settings, defaultSignature, values, logoUrl, signatureUrl]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="grid gap-6 xl:grid-cols-[1fr_minmax(0,480px)]">
        <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invoice Numbering</CardTitle>
              <CardDescription>
                Format: <code className="text-foreground">{'{companyPrefix}-{marker}-{year}-{seq}'}</code>.
                Company prefix (<span className="font-medium text-foreground">{settings?.companyPrefix || 'none'}</span>) is set on the General tab.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="invoicePrefix" label="Invoice Type Marker" description="e.g. INV" />
                <FormField form={form} name="nextInvoiceSequence" label="Next Invoice Number" type="number" description="Auto-bumps after each invoice." />
              </div>
              <NumberPreview kind="Invoice" company={settings?.companyPrefix || ''} marker={values.invoicePrefix || 'INV'} seq={Number(values.nextInvoiceSequence) || 1} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Footer</CardTitle></CardHeader>
            <CardContent>
              <FormField form={form} name="invoiceFooterText" label="Invoice Footer" placeholder="Thank you for your business!" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Default Payment Info</CardTitle><CardDescription>Pre-fills new invoices. Each invoice can override these at creation time.</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="bankName" label="Bank Name" />
                <FormField form={form} name="bankAccountName" label="Account Holder" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="bankAccountNumber" label="Account Number" />
                <FormField form={form} name="bankIfsc" label="IFSC" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="bankSwift" label="SWIFT" />
                <FormField form={form} name="upiId" label="UPI ID" />
              </div>
              <FormField form={form} name="paypalEmail" label="PayPal Email" type="email" />
              <FormTextarea form={form} name="paymentInstructions" label="Additional Instructions" />
            </CardContent>
          </Card>

          <Separator />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => form.reset()}>Reset</Button>
            <Button type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save Invoice Settings'}</Button>
          </div>
        </div>

        <div className="min-w-0">
          <div className="sticky top-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Live Preview</CardTitle><CardDescription>How the generated invoice PDF will look</CardDescription></CardHeader>
              <CardContent className="bg-slate-100 dark:bg-slate-900 rounded-b-lg pt-4 pb-6">
                <InvoiceTemplatePreview {...previewProps} />
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground mt-2 px-1">Branding (logo, theme, company info) comes from the General tab. Edit it there to update this preview.</p>
          </div>
        </div>
      </div>
    </form>
  );
}
